/**
 * Closest Point of Approach (CPA) and Time to CPA (TCPA) between own ship and an
 * AIS target. Worked in a local flat-earth frame in nautical miles (fine over the
 * few miles AIS matters), with velocities in knots. Standard relative-motion
 * solution: project the relative position onto the relative velocity.
 *
 * TCPA is positive when the CPA is in the future (closing), negative when it's
 * already behind us (opening). CPA is the miss distance at that moment.
 */
export interface Kinematic {
  /** [lon, lat]. */
  position: [number, number];
  /** Course over ground, degrees true. */
  cogDeg: number;
  /** Speed over ground, knots. */
  sogKn: number;
}

export interface CpaResult {
  /** Miss distance at CPA, nautical miles. */
  cpaNm: number;
  /** Minutes to CPA: positive = closing/future, negative = past/opening. */
  tcpaMin: number;
  /** Current range now, nautical miles. */
  rangeNm: number;
}

const D2R = Math.PI / 180;

/** Velocity components (east, north) in knots from COG/SOG. */
function vel(k: Kinematic): [number, number] {
  return [k.sogKn * Math.sin(k.cogDeg * D2R), k.sogKn * Math.cos(k.cogDeg * D2R)];
}

export function computeCpa(own: Kinematic, tgt: Kinematic): CpaResult {
  // Relative position of target from own, in nm (east, north).
  const latMid = ((own.position[1] + tgt.position[1]) / 2) * D2R;
  const rx = (tgt.position[0] - own.position[0]) * 60 * Math.cos(latMid);
  const ry = (tgt.position[1] - own.position[1]) * 60;
  const rangeNm = Math.hypot(rx, ry);

  const [ovx, ovy] = vel(own);
  const [tvx, tvy] = vel(tgt);
  const dvx = tvx - ovx;
  const dvy = tvy - ovy;
  const dv2 = dvx * dvx + dvy * dvy;

  if (dv2 < 1e-9) {
    // No relative motion: range is constant, CPA is now.
    return { cpaNm: rangeNm, tcpaMin: 0, rangeNm };
  }
  const tcpaH = -(rx * dvx + ry * dvy) / dv2; // hours
  const cx = rx + dvx * tcpaH;
  const cy = ry + dvy * tcpaH;
  return { cpaNm: Math.hypot(cx, cy), tcpaMin: tcpaH * 60, rangeNm };
}
